import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { StarIcon, MapPinIcon } from 'lucide-react';
import { contact, heroImage } from '../data/site';
import { useLanguage } from '../contexts/LanguageContext';
import { useMotionPreferences } from '../hooks/useMotionPreferences';
import { GlowButton } from './motion/GlowButton';
import { fadeUp, staggerContainer } from '../utils/motion';
export function Hero() {
  const sectionRef = useRef<HTMLElement>(null);
  const {
    reduced,
    parallax
  } = useMotionPreferences();
  const {
    t
  } = useLanguage();
  const {
    scrollYProgress
  } = useScroll({
    target: sectionRef,
    offset: ['start start', 'end start']
  });

  // Background drifts slower than the foreground copy — classic depth parallax.
  const backgroundY = useTransform(scrollYProgress, [0, 1], ['0%', `${18 * parallax}%`]);
  const backgroundScale = useTransform(scrollYProgress, [0, 1], [1, 1 + 0.08 * parallax]);
  const contentY = useTransform(scrollYProgress, [0, 1], [0, -110 * parallax]);
  const contentScale = useTransform(scrollYProgress, [0, 1], [1, reduced ? 1 : 0.94]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.65], [1, 0]);
  const auraY = useTransform(scrollYProgress, [0, 1], [0, 60 * parallax]);
  const item = fadeUp(reduced, 26);
  return <section id="home" ref={sectionRef} className="relative flex min-h-screen w-full items-center overflow-hidden bg-navy-deep pt-28 pb-20" data-id="element-92" data-mp="3e8mt707kwpf5">
      <motion.div style={{
      y: backgroundY,
      scale: backgroundScale,
      willChange: 'transform'
    }} className="absolute inset-0" data-id="element-93" data-mp="358rbsdnfjy6o">
        <img src={heroImage} alt="Neon-lit padel court at Pulse Padel Hub" className="h-full w-full object-cover opacity-45" data-id="element-94" data-mp="2ql6a8o67e70v" />
      </motion.div>

      <div className="absolute inset-0 bg-gradient-to-b from-[#07051A]/85 via-[#0E0B2C]/80 to-[#07051A]" data-id="element-95" data-mp="2hix1o3uhwnq4" />
      <div className="grid-lines absolute inset-0 opacity-60" data-id="element-96" data-mp="2ljpch1j0n0oi" />

      <motion.div aria-hidden="true" style={{
      y: auraY,
      willChange: 'transform'
    }} className="pointer-events-none absolute inset-0" data-id="element-97" data-mp="s1rqcip57wrn">
        <motion.div animate={reduced ? undefined : {
        scale: [1, 1.18, 1],
        opacity: [0.55, 0.85, 0.55]
      }} transition={{
        duration: 9,
        repeat: Infinity,
        ease: 'easeInOut'
      }} className="absolute -left-40 top-1/4 h-[28rem] w-[28rem] rounded-full bg-electric/25 blur-[140px]" data-id="element-98" data-mp="na09x0zpb7f8" />
        <motion.div animate={reduced ? undefined : {
        scale: [1.1, 0.95, 1.1],
        opacity: [0.5, 0.85, 0.5]
      }} transition={{
        duration: 11,
        repeat: Infinity,
        ease: 'easeInOut'
      }} className="absolute -right-32 bottom-0 h-[32rem] w-[32rem] rounded-full bg-magenta/25 blur-[150px]" data-id="element-99" data-mp="1ayt68yk8jhad" />
        <motion.div animate={reduced ? undefined : {
        scale: [0.95, 1.15, 0.95],
        opacity: [0.45, 0.75, 0.45]
      }} transition={{
        duration: 13,
        repeat: Infinity,
        ease: 'easeInOut'
      }} className="absolute left-1/2 top-1/3 h-80 w-80 -translate-x-1/2 rounded-full bg-grape/25 blur-[130px]" data-id="element-100" data-mp="3lsyrwwxreii0" />
      </motion.div>

      <div className="relative mx-auto w-full max-w-7xl px-5 sm:px-8" data-id="element-101" data-mp="21bxyn68yqubv">
        <motion.div variants={staggerContainer(0.11, 0.1)} initial="hidden" animate="show" style={{
        y: contentY,
        scale: contentScale,
        opacity: contentOpacity,
        willChange: 'transform, opacity'
      }} className="max-w-3xl" data-id="element-102" data-mp="26a98vi3676um">
          <motion.div variants={item} className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-2 backdrop-blur-md" data-id="element-103" data-mp="2kxkj9gnm8kt5">
            <StarIcon className="h-4 w-4 fill-magenta text-magenta" data-id="element-104" data-mp="2tyino01yuuc9" />
            <span className="text-xs font-semibold uppercase tracking-[0.18em] text-white/85" data-id="element-105" data-mp="umpsm8yqnz26">
              {t.hero.badge}
            </span>
          </motion.div>

          <div className="relative" data-id="element-106" data-mp="znn8ug06uj11">
            <motion.div aria-hidden="true" animate={reduced ? undefined : {
            scale: [0.9, 1.08, 0.9],
            opacity: [0.35, 0.6, 0.35]
          }} transition={{
            duration: 7,
            repeat: Infinity,
            ease: 'easeInOut'
          }} style={{
            willChange: 'transform, opacity'
          }} className="pointer-events-none absolute -left-16 top-6 -z-10 h-64 w-[34rem] max-w-[120%] rounded-full bg-gradient-to-r from-electric/40 via-magenta/40 to-grape/40 blur-[110px]" data-id="element-107" data-mp="4aupet2693ou" />

            <motion.h1 variants={item} className="neon-text mt-7 font-display text-[2.7rem] font-black uppercase leading-[0.95] tracking-tight text-white sm:text-6xl lg:text-7xl" data-id="element-108" data-mp="3csqv5ppu049k">
              {t.hero.titleLines[0]}
              <br data-id="element-109" data-mp="2yz9bm4x5arjj" />
              {t.hero.titleLines[1]}
              <br data-id="element-110" data-mp="2yxohggeqtam7" />
              <span className="bg-gradient-to-r from-electric via-magenta to-grape bg-clip-text text-transparent" data-id="element-111" data-mp="2u0ppa3en3dqn">
                {t.hero.titleLines[2]}
              </span>
            </motion.h1>
          </div>

          <motion.p variants={item} className="mt-7 max-w-xl text-base leading-relaxed text-white/70 sm:text-lg" data-id="element-112" data-mp="1e93zjvodwuqq">
            {t.hero.subtitle}
          </motion.p>

          <motion.div variants={item} className="mt-10 flex flex-col gap-4 sm:flex-row sm:items-center" data-id="element-113" data-mp="14javdfqknhwi">
            <GlowButton href={contact.bookingHref} className="inline-flex items-center justify-center rounded-full bg-magenta px-9 py-4 font-display text-sm font-extrabold uppercase tracking-[0.16em] text-white shadow-glow-magenta transition-shadow duration-300 hover:shadow-[0_0_70px_-6px_rgba(236,30,156,0.95)]" data-id="element-114" data-mp="1s9ogsneniii4">
              {t.hero.cta}
            </GlowButton>
          </motion.div>

          <motion.div variants={item} className="mt-10 flex items-center gap-2 text-sm text-white/50" data-id="element-115" data-mp="7vo7453nj5u5">
            <MapPinIcon className="h-4 w-4 text-electric" data-id="element-116" data-mp="2ehtv44tx3e3k" />
            {contact.address}
          </motion.div>
        </motion.div>
      </div>

      <motion.div aria-hidden="true" style={{
      opacity: contentOpacity
    }} className="absolute bottom-8 left-1/2 hidden -translate-x-1/2 lg:block" data-id="element-117" data-mp="29j948w0k26rs">
        <motion.span animate={reduced ? undefined : {
        y: [0, 10, 0],
        opacity: [0.4, 1, 0.4]
      }} transition={{
        duration: 2.4,
        repeat: Infinity,
        ease: 'easeInOut'
      }} className="block h-12 w-px bg-gradient-to-b from-transparent via-magenta to-transparent" style={{
        willChange: 'transform, opacity'
      }} data-id="element-118" data-mp="1zrwfida1zwpz" />
      </motion.div>
    </section>;
}